import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AdminMenuItem,
  AdminPageQuery,
  AdminPostQuery,
  CategorySummary,
  ChangePasswordInput,
  CreateInquiryInput,
  CreateMenuItemInput,
  DashboardSummary,
  InquiryNotifications,
  InquiryQuery,
  InquirySummary,
  LoginInput,
  MediaAssetSummary,
  MediaQuery,
  PageDetail,
  PageSummary,
  Paginated,
  PostDetail,
  PostListItem,
  PublicPage,
  PublicPostQuery,
  ReorderMenuInput,
  SessionUser,
  SetPageVisibilityInput,
  SettingValues,
  SiteManifest,
  TermSummary,
  UpdateMenuItemInput,
  UpdatePostInput,
  UserQuery,
  UserSummary,
} from '@tbbt/shared';
import { api } from './client';

/**
 * Query keys, centralised.
 *
 * A single hierarchy means invalidation can be expressed by prefix — invalidating
 * `['posts']` refreshes every post list and detail at once — instead of every mutation
 * having to enumerate the keys it might have affected and inevitably missing one.
 */
export const queryKeys = {
  manifest: ['manifest'] as const,
  publicPosts: (query: PublicPostQuery) => ['public', 'posts', query] as const,
  publicPost: (slug: string, preview?: string) =>
    ['public', 'post', slug, preview ?? null] as const,
  publicPage: (path: string) => ['public', 'page', path] as const,
  publicCategories: ['public', 'categories'] as const,
  publicTags: ['public', 'tags'] as const,

  session: ['session'] as const,
  dashboard: ['dashboard'] as const,

  posts: (query: AdminPostQuery) => ['posts', query] as const,
  post: (id: string) => ['posts', id] as const,
  pages: (query: AdminPageQuery) => ['pages', query] as const,
  pageStructure: ['pages', 'structure'] as const,
  page: (id: string) => ['pages', id] as const,
  navigation: ['navigation'] as const,
  settings: ['settings'] as const,
  media: (query: MediaQuery) => ['media', query] as const,
  mediaAsset: (id: string) => ['media', 'asset', id] as const,
  inquiries: (query: InquiryQuery) => ['inquiries', query] as const,
  // Under the same 'inquiries' root, so a status change invalidates both the list and any
  // open detail view with one call.
  inquiry: (id: string) => ['inquiries', 'detail', id] as const,
  /**
   * The notification bell's payload.
   *
   * Outside the `inquiries` root, deliberately. It is polled on a timer from every admin
   * screen, and under that root every list invalidation — a status change, a search — would
   * drag a second request along with it.
   */
  inquiryNotifications: ['notifications', 'inquiries'] as const,
  users: (query: UserQuery) => ['users', query] as const,
  categories: ['categories'] as const,
  tags: ['tags'] as const,
} as const;

// -----------------------------------------------------------------------------
// Public
// -----------------------------------------------------------------------------

/**
 * The site manifest — navigation, settings and the route table.
 *
 * This used to be held for five minutes and never refetched on focus, on the grounds that
 * it changes only when an admin saves and that refetching on every tab switch would
 * re-render the whole shell for nothing. Both halves of that turned out to be wrong in the
 * case that matters most.
 *
 * The case is an admin with two tabs open — the console in one, their live site in the
 * other. They turn a page on, switch tabs, and the menu is unchanged, because this query
 * is a different `QueryClient` from the console's and nothing told it anything had
 * happened. A five-minute stale time meant switching back did not help either.
 *
 * Thirty seconds and a refetch on focus fixes that, and the "for no reason" objection no
 * longer holds:
 *
 *  - The server now answers this endpoint with `no-cache` and an ETag, so an unchanged
 *    manifest is a `304` with an empty body rather than 7.5KB. See PUBLIC_CONTENT_CACHE.
 *  - TanStack's structural sharing keeps the previous object when the response is deeply
 *    equal, so an unchanged refetch does not change `data`'s identity and consumers do not
 *    re-render. The re-render only happens when something actually changed — which is
 *    precisely when it should.
 *
 * Thirty seconds rather than zero so that ordinary navigation around the site, which
 * remounts this hook's consumers, does not issue a request per page view.
 */
export function useSiteManifest(): UseQueryResult<SiteManifest> {
  return useQuery({
    queryKey: queryKeys.manifest,
    queryFn: () => api.get<SiteManifest>('/public/manifest'),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function usePublicPosts(query: PublicPostQuery): UseQueryResult<Paginated<PostListItem>> {
  return useQuery({
    queryKey: queryKeys.publicPosts(query),
    queryFn: () =>
      api.get<Paginated<PostListItem>>('/public/posts', {
        query: {
          page: query.page,
          limit: query.limit,
          q: query.q,
          category: query.category,
          tag: query.tag,
        },
      }),
    staleTime: 60 * 1000,
    // Keeps the previous page visible while the next one loads, instead of flashing
    // an empty list on every pagination click.
    placeholderData: (previous) => previous,
  });
}

export type PublicPostResponse = PostDetail & { seo: { title: string; description: string } };

export function usePublicPost(
  slug: string,
  previewToken?: string,
): UseQueryResult<PublicPostResponse> {
  return useQuery({
    queryKey: queryKeys.publicPost(slug, previewToken),
    queryFn: () =>
      api.get<PublicPostResponse>(`/public/posts/${slug}`, {
        query: previewToken ? { preview: previewToken } : undefined,
      }),
    // A missing article is a real answer, not a transient failure — retrying four
    // times just delays the 404 page.
    retry: false,
  });
}

export function usePublicPage(path: string): UseQueryResult<PublicPage> {
  return useQuery({
    queryKey: queryKeys.publicPage(path),
    queryFn: () => api.get<PublicPage>(path === '' ? '/public/page' : `/public/page/${path}`),
    retry: false,
    staleTime: 60 * 1000,
  });
}

export function usePublicCategories(): UseQueryResult<CategorySummary[]> {
  return useQuery({
    queryKey: queryKeys.publicCategories,
    queryFn: () => api.get<CategorySummary[]>('/public/categories'),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePublicTags(): UseQueryResult<TermSummary[]> {
  return useQuery({
    queryKey: queryKeys.publicTags,
    queryFn: () => api.get<TermSummary[]>('/public/tags'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSubmitInquiry() {
  return useMutation({
    mutationFn: (input: CreateInquiryInput) =>
      api.post<{ id: string | null; accepted: true }>('/public/inquiries', input),
  });
}

// -----------------------------------------------------------------------------
// Session
// -----------------------------------------------------------------------------

export function useSession(): UseQueryResult<SessionUser> {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api.get<SessionUser>('/auth/me'),
    // A 401 here is the expected answer for a signed-out visitor, so retrying is
    // wasted work and delays showing the login form.
    retry: false,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => api.post<{ user: SessionUser }>('/auth/login', input),
    onSuccess: ({ user }) => {
      // Seed the cache directly rather than refetching — the response already
      // contains exactly what `useSession` would ask for.
      queryClient.setQueryData(queryKeys.session, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      // Clear everything: cached admin data belongs to the account that just signed
      // out and must not be visible to whoever signs in next on this device.
      queryClient.clear();
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.post<{ user: SessionUser }>('/auth/change-password', input),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(queryKeys.session, user);
    },
  });
}

// -----------------------------------------------------------------------------
// Admin — dashboard
// -----------------------------------------------------------------------------

export function useDashboard(): UseQueryResult<DashboardSummary> {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => api.get<DashboardSummary>('/admin/dashboard'),
    staleTime: 30 * 1000,
    /**
     * The one query that refetches when the tab regains focus.
     *
     * The global default is `false`, and for the public site that is right — content
     * cached for a minute cannot return anything newer. The dashboard is different: it
     * is the activity screen, it feeds the sidebar's unanswered-enquiry badge on every
     * admin route, and the figures it shows move because of things that happen off this
     * tab entirely — a visitor submitting an enquiry, a scheduled post going live, a
     * colleague publishing. Coming back to a console that still shows the count from
     * twenty minutes ago is the specific complaint this answers.
     *
     * Scoped here rather than flipped globally, and paired with the 30-second stale
     * time, so switching tabs twice in a row costs one request rather than two.
     */
    refetchOnWindowFocus: true,
  });
}

// -----------------------------------------------------------------------------
// Admin — posts
// -----------------------------------------------------------------------------

export function useAdminPosts(query: AdminPostQuery): UseQueryResult<Paginated<PostListItem>> {
  return useQuery({
    queryKey: queryKeys.posts(query),
    queryFn: () =>
      api.get<Paginated<PostListItem>>('/admin/posts', {
        query: {
          page: query.page,
          limit: query.limit,
          q: query.q,
          status: query.status,
          categoryId: query.categoryId,
          tagId: query.tagId,
          authorId: query.authorId,
          sortBy: query.sortBy,
          sortDir: query.sortDir,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useAdminPost(id: string | undefined): UseQueryResult<PostDetail> {
  return useQuery({
    queryKey: queryKeys.post(id ?? 'new'),
    queryFn: () => api.get<PostDetail>(`/admin/posts/${id}`),
    enabled: Boolean(id),
  });
}

export function useUpdatePost(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdatePostInput) => api.patch<PostDetail>(`/admin/posts/${id}`, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.post(id), updated);
      // Prefix invalidation: refreshes every filtered list without enumerating them.
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: unknown) => api.post<PostDetail>('/admin/posts', input),
    onSuccess: (created) => {
      /*
        Seed the new post's detail cache before the editor navigates to it.

        `PostEditorPage` redirects to `/admin/posts/:id` the moment this resolves, and
        without this the screen it lands on has an empty cache: it shows its loading
        state and refetches a post the server just handed us. Seeding makes the
        transition instant, and the prefix invalidation below still marks the entry
        stale so a background refetch reconciles anything the server computed —
        which is the same stale-while-revalidate contract `useUpdatePost` relies on.
      */
      queryClient.setQueryData(queryKeys.post(created.id), created);
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useSetPostEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      api.patch<PostListItem>(`/admin/posts/${id}/enabled`, { isEnabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
      /*
        The dashboard lists recent posts with a status chip, and that chip reads both
        `status` and `isEnabled` — a published-but-disabled post shows as "Hidden".
        Without this the dashboard keeps claiming a post is live after it was hidden,
        which is exactly the mistake `PostStatusChip` exists to prevent.
      */
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useReorderPosts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) =>
      api.patch<void>('/admin/posts/reorder', { items }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useCreatePreviewLink() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ url: string; expiresAt: string }>(`/admin/posts/${id}/preview-link`),
  });
}

// -----------------------------------------------------------------------------
// Admin — pages and navigation
// -----------------------------------------------------------------------------

export function usePageStructure(): UseQueryResult<PageSummary[]> {
  return useQuery({
    queryKey: queryKeys.pageStructure,
    queryFn: () => api.get<PageSummary[]>('/admin/pages/structure'),
  });
}

export function useAdminPage(id: string | undefined): UseQueryResult<PageDetail> {
  return useQuery({
    queryKey: queryKeys.page(id ?? 'new'),
    queryFn: () => api.get<PageDetail>(`/admin/pages/${id}`),
    enabled: Boolean(id),
  });
}

export function useSetPageVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SetPageVisibilityInput) =>
      api.put<PageSummary[]>('/admin/pages/visibility', input),
    onSuccess: (pages) => {
      queryClient.setQueryData(queryKeys.pageStructure, pages);
      // The public manifest carries the route table, so hiding a page must
      // invalidate it or the site keeps serving the old routes until it expires.
      void queryClient.invalidateQueries({ queryKey: queryKeys.manifest });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useNavigation(): UseQueryResult<AdminMenuItem[]> {
  return useQuery({
    queryKey: queryKeys.navigation,
    queryFn: () => api.get<AdminMenuItem[]>('/admin/navigation'),
  });
}

/**
 * The three menu mutations share one invalidation, which is the part worth naming:
 * `manifest` as well as `navigation`. The admin list and the public site read the
 * same rows through different endpoints, so refreshing only the admin's copy would
 * leave the live header showing the previous arrangement.
 */
function useMenuInvalidation(): () => void {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.navigation });
    void queryClient.invalidateQueries({ queryKey: queryKeys.manifest });
  };
}

/**
 * Persist a menu's arrangement, applied to the cache before the server answers.
 *
 * The optimistic write is what makes a drag feel like a drag. Without it the dropped
 * row springs back to where it came from and then jumps forward again when the round
 * trip lands — the list would look like it had rejected the move. The previous cache is
 * kept so a failed write puts the menu back exactly as it was rather than leaving the
 * screen showing an order the server never accepted.
 */
export function useReorderMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ReorderMenuInput) =>
      api.put<AdminMenuItem[]>('/admin/navigation/reorder', input),
    onMutate: (input) => {
      const previous = queryClient.getQueryData<AdminMenuItem[]>(queryKeys.navigation);
      if (!previous) return { previous: undefined };

      const moved = new Map(input.items.map((item) => [item.id, item]));
      queryClient.setQueryData<AdminMenuItem[]>(
        queryKeys.navigation,
        previous.map((item) => {
          const patch = moved.get(item.id);
          return patch ? { ...item, sortOrder: patch.sortOrder, parentId: patch.parentId } : item;
        }),
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.navigation, context.previous);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.manifest });
    },
    // Reconciled on settle rather than on success, so the list ends up on server truth
    // whichever way the write went.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.navigation });
    },
  });
}

export function useCreateMenuItem() {
  const invalidate = useMenuInvalidation();

  return useMutation({
    mutationFn: (input: CreateMenuItemInput) => api.post<AdminMenuItem>('/admin/navigation', input),
    onSuccess: invalidate,
  });
}

export function useUpdateMenuItem() {
  const invalidate = useMenuInvalidation();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateMenuItemInput & { id: string }) =>
      api.patch<AdminMenuItem>(`/admin/navigation/${id}`, input),
    onSuccess: invalidate,
  });
}

// -----------------------------------------------------------------------------
// Admin — settings, media, inquiries, users
// -----------------------------------------------------------------------------

export function useSettings(): UseQueryResult<SettingValues> {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.get<SettingValues>('/admin/settings'),
  });
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.put<unknown>(`/admin/settings/${key}`, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      void queryClient.invalidateQueries({ queryKey: queryKeys.manifest });
    },
  });
}

export function useMedia(query: MediaQuery): UseQueryResult<Paginated<MediaAssetSummary>> {
  return useQuery({
    queryKey: queryKeys.media(query),
    queryFn: () =>
      api.get<Paginated<MediaAssetSummary>>('/admin/media', {
        query: { page: query.page, limit: query.limit, q: query.q },
      }),
    placeholderData: (previous) => previous,
  });
}

/**
 * One asset by id.
 *
 * Used by the post editor to describe the chosen cover image — its filename and
 * dimensions — without having to find it again in a paginated list it may not be on.
 */
export function useMediaAsset(id: string | null | undefined): UseQueryResult<MediaAssetSummary> {
  return useQuery({
    queryKey: queryKeys.mediaAsset(id ?? 'none'),
    queryFn: () => api.get<MediaAssetSummary>(`/admin/media/${id}`),
    enabled: Boolean(id),
    // An asset's metadata does not change on its own; refetching it while somebody
    // writes a post is pure noise.
    staleTime: 5 * 60 * 1000,
  });
}

export function useUploadMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, altText }: { file: File; altText: string }) => {
      // FormData, not JSON: the client detects it and omits the Content-Type header so
      // the browser can set the multipart boundary itself.
      const form = new FormData();
      form.append('file', file);
      form.append('altText', altText);
      return api.post<MediaAssetSummary>('/admin/media', form);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['media'] });
      // The dashboard counts files and sums their bytes, and it is the storage figure
      // that drives the "media storage is growing" warning. `useDeleteMedia` already
      // invalidated it; an upload moves the same two numbers.
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

/**
 * Delete an asset.
 *
 * The API refuses with 409 while the image is still referenced by a post cover, a page's
 * social image, or an article body — so the caller's job is to report that refusal, not
 * to pre-empt it. Guessing client-side would be both slower and less accurate.
 */
export function useDeleteMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/media/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['media'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useInquiries(query: InquiryQuery): UseQueryResult<Paginated<InquirySummary>> {
  return useQuery({
    queryKey: queryKeys.inquiries(query),
    queryFn: () =>
      api.get<Paginated<InquirySummary>>('/admin/inquiries', {
        query: { page: query.page, limit: query.limit, q: query.q, status: query.status },
      }),
    placeholderData: (previous) => previous,
  });
}

/**
 * One enquiry, by id — for the screen the notification email links to.
 *
 * Fetched rather than picked out of a cached list page: an emailed link is followed days
 * later, by which point the enquiry may be on any page of any filter.
 */
export function useInquiry(id: string | undefined): UseQueryResult<InquirySummary> {
  return useQuery({
    queryKey: queryKeys.inquiry(id ?? 'none'),
    queryFn: () => api.get<InquirySummary>(`/admin/inquiries/${id}`),
    enabled: Boolean(id),
  });
}

export function useUpdateInquiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string | null }) =>
      api.patch<InquirySummary>(`/admin/inquiries/${id}`, { status, notes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      // The API files a read receipt when an enquiry is triaged, so the bell has changed
      // even though nothing on this screen asked it to.
      void queryClient.invalidateQueries({ queryKey: queryKeys.inquiryNotifications });
    },
  });
}

/**
 * Unread enquiries for the signed-in user — the bell.
 *
 * Polled rather than pushed. There is no websocket in this deployment, and a minute's
 * latency on "an enquiry arrived" is acceptable for an inbox somebody checks by hand;
 * a persistent connection per admin tab would not be. `enabled` is the caller's job,
 * because an author has no INQUIRY_READ permission and polling a 403 every minute is
 * both useless and noisy in the logs.
 */
export function useInquiryNotifications(enabled: boolean): UseQueryResult<InquiryNotifications> {
  return useQuery({
    queryKey: queryKeys.inquiryNotifications,
    queryFn: () => api.get<InquiryNotifications>('/admin/notifications'),
    enabled,
    /*
      A minute on the timer, and refetched when the tab comes back. The second half is
      what covers the case the first half handles badly: a console left open in a
      background tab, where browsers throttle timers heavily. Together they mean the
      badge is right within a minute of looking at it, which is the only moment it
      matters.
    */
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
    // A failed poll is not worth reporting: the next one is thirty seconds away, and the
    // stale count on screen is a better answer than an error toast every minute of an
    // outage.
    retry: false,
  });
}

/**
 * Record that the signed-in user has opened an enquiry.
 *
 * The list and detail caches are patched in place rather than invalidated, so opening an
 * enquiry does not refetch the page you are looking at — the one field that changed is
 * `isRead`, and the server has nothing else new to tell us. The bell *is* refetched,
 * because its count and its list both changed.
 */
export function useMarkInquiryRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/admin/inquiries/${id}/read`),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<InquirySummary>(queryKeys.inquiry(id), (previous) =>
        previous ? { ...previous, isRead: true } : previous,
      );

      queryClient.setQueriesData<Paginated<InquirySummary>>(
        { queryKey: ['inquiries'] },
        (previous) =>
          // Guarded: the same prefix also holds the detail entries patched above, which
          // have no `items` and would throw on `.map`.
          previous && Array.isArray(previous.items)
            ? {
                ...previous,
                items: previous.items.map((item) =>
                  item.id === id ? { ...item, isRead: true } : item,
                ),
              }
            : previous,
      );

      void queryClient.invalidateQueries({ queryKey: queryKeys.inquiryNotifications });
    },
  });
}

/** Clear the bell in one go. Returns the emptied payload, which seeds the cache. */
export function useMarkAllInquiriesRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<InquiryNotifications>('/admin/inquiries/read-all'),
    onSuccess: (notifications) => {
      queryClient.setQueryData(queryKeys.inquiryNotifications, notifications);
      // Every row's `isRead` moved, and there is no telling which page is cached, so this
      // one really is a refetch rather than a patch.
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
}

export function useUsers(query: UserQuery): UseQueryResult<Paginated<UserSummary>> {
  return useQuery({
    queryKey: queryKeys.users(query),
    queryFn: () =>
      api.get<Paginated<UserSummary>>('/admin/users', {
        query: { page: query.page, limit: query.limit, q: query.q, role: query.role },
      }),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { email: string; name: string; role: string }) =>
      api.post<{ inviteUrl: string; expiresAt: string }>('/auth/invite', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
